/**
 * TABLE CELL COMPONENT
 * Smart cell renderer with auto-formatting
 * @version 1.0.0
 * @production-ready
 */

import React, { useEffect, useRef } from 'react';
import { ColumnDefinition, TableStyleConfig } from '../types';
import { formatCellValue, formatDateDisplay } from '../utils';
import { CustomSelect } from './CustomSelect';
import { CustomTimeInput } from './CustomTimeInput';

interface TableCellProps {
  column: ColumnDefinition;
  value: any;
  rowData: any;
  width: number;
  styleConfig: TableStyleConfig;
  isSelected: boolean;
  isEditable: boolean;
  onChange: (value: any) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onFocus: () => void;
  inputRef: (el: HTMLInputElement | null) => void;
  onRemoveRowKey?: () => void;
}

const TableCellComponent: React.FC<TableCellProps> = ({
  column,
  value,
  rowData,
  width,
  styleConfig,
  isSelected,
  isEditable,
  onChange,
  onKeyDown,
  onFocus,
  inputRef,
  onRemoveRowKey
}) => {
  const condensed = styleConfig.condensed ?? true;
  const rowHeight = styleConfig.rowHeight ?? (condensed ? 28 : 38);
  const fontSize = styleConfig.baseFontSize ?? (condensed ? 11 : 14);
  const padding = styleConfig.padding ?? (condensed ? '2px 4px' : '8px 12px');

  // Render action/delete button
  // NOTE: This only removes the row from frontend state temporarily.
  // It does NOT delete data from the backend/database.
  if (column.type === 'action' && column.key === '_delete') {
    const buttonStyle: React.CSSProperties = {
      width: '100%',
      height: `${rowHeight}px`,
      padding: 0,
      margin: 0,
      border: 'none',
      background: 'transparent',
      color: '#dc2626',
      fontSize: `${fontSize + 2}px`,
      fontWeight: 700,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: styleConfig.fontFamily,
      transition: 'all 0.2s ease',
      outline: 'none'
    };

    return (
      <button
        type="button"
        style={buttonStyle}
        onClick={(e) => {
          e.stopPropagation();
          if (onRemoveRowKey) {
            onRemoveRowKey();
          }
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = '#fee2e2';
          e.currentTarget.style.color = '#991b1b';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.color = '#dc2626';
        }}
        title="Remove row from view (frontend only, does not delete from database)"
      >
        ×
      </button>
    );
  }

  // Use 400 (normal) for ALL inputs - remove boldness from all input boxes
  // Force 400 for all inputs regardless of column definition or calculated/readonly status
  const inputFontWeight = 400; // Always use 400 for all inputs

  const baseStyle: React.CSSProperties = {
    padding,
    fontSize: `${fontSize}px`,
    height: `${rowHeight}px`,
    width: '100%',
    border: isSelected 
      ? '2px solid var(--color-primary-blue)' 
      : '1px solid var(--color-gray-200)',
    outline: isSelected ? '2px solid var(--color-primary-blue-light)' : 'none',
    background: column.calculated 
      ? 'var(--color-primary-blue-light)' 
      : value 
        ? 'var(--color-white)' 
        : 'var(--color-gray-50)',
    color: column.calculated 
      ? 'var(--color-primary-blue)' 
      : '#000000',
    fontWeight: inputFontWeight,
    boxSizing: 'border-box',
    textAlign: column.textAlign || 'center',
    fontFamily: styleConfig.fontFamily
  };

  // Render time input - USING CUSTOM COMPONENT TO REMOVE BORDERS FROM POPUP
  if (column.type === 'time') {
    const timeStyle: React.CSSProperties = {
      padding,
      fontSize: `${fontSize}px`,
      height: `${rowHeight}px`,
      width: '100%',
      border: 'none',
      borderWidth: 0,
      borderStyle: 'none',
      borderColor: 'transparent',
      borderLeft: 'none',
      outline: 'none',
      boxShadow: 'none',
      background: column.calculated 
        ? 'var(--color-primary-blue-light)' 
        : value 
          ? 'var(--color-white)' 
          : 'var(--color-gray-50)',
      color: column.calculated 
        ? 'var(--color-primary-blue)' 
        : '#000000',
      fontWeight: 400,
      boxSizing: 'border-box',
      textAlign: column.textAlign || 'center',
      fontFamily: styleConfig.fontFamily
    };

    return (
      <CustomTimeInput
        value={value || ''}
        onChange={onChange}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        onBlur={() => {}}
        disabled={column.disabled}
        readOnly={!isEditable}
        title={column.description || column.label}
        style={timeStyle}
        className="custom-time-input-no-border"
        inputRef={inputRef}
      />
    );
  }

  // Render date input
  if (column.type === 'date') {
    const dateInputRef = useRef<HTMLInputElement>(null);
    const previousValueRef = useRef<string>(value || '');
    const pickerOpenedAtRef = useRef<number>(0);
    const formattedDisplay = formatDateDisplay(value);
    const placeholderText = column.placeholder || 'DD/MMM/YYYY';
    const justifyContent = column.textAlign === 'left'
      ? 'flex-start'
      : column.textAlign === 'right'
        ? 'flex-end'
        : 'center';
    const textColor = column.calculated ? 'var(--color-primary-blue)' : '#000000';
    const placeholderColor = 'rgba(0,0,0,0.5)';
    const backgroundColor = column.calculated 
      ? 'var(--color-primary-blue-light)'
      : value
        ? 'var(--color-white)'
        : 'var(--color-gray-50)';

    const overlayTextRef = useRef<HTMLSpanElement>(null);

    // Update previous value ref when value prop changes (from parent)
    useEffect(() => {
      previousValueRef.current = value || '';
    }, [value]);

    useEffect(() => {
      if (dateInputRef.current) {
        // Completely hide the native date input value while keeping it functional
        dateInputRef.current.style.setProperty('font-weight', '400', 'important');
        dateInputRef.current.style.setProperty('color', 'transparent', 'important');
        dateInputRef.current.style.setProperty('caret-color', 'transparent', 'important');
        // Move text off-screen but keep input functional
        dateInputRef.current.style.setProperty('text-indent', '-9999px', 'important');
        // Hide any browser-specific text fill
        dateInputRef.current.style.setProperty('-webkit-text-fill-color', 'transparent', 'important');
        dateInputRef.current.style.setProperty('-moz-text-fill-color', 'transparent', 'important');
        // Ensure input remains clickable and functional
        dateInputRef.current.style.setProperty('opacity', '1', 'important');
        dateInputRef.current.style.setProperty('pointer-events', 'auto', 'important');
      }
      // Force normal font weight on overlay text - multiple attempts to ensure it works
      if (overlayTextRef.current) {
        overlayTextRef.current.style.setProperty('font-weight', '400', 'important');
        overlayTextRef.current.style.setProperty('font-style', 'normal', 'important');
      }
      // Also try via querySelector as backup
      const overlayEl = document.querySelector(`.date-display-text[data-date="${value || ''}"]`);
      if (overlayEl) {
        (overlayEl as HTMLElement).style.setProperty('font-weight', '400', 'important');
        (overlayEl as HTMLElement).style.setProperty('font-style', 'normal', 'important');
      }
    }, [value, isSelected]);

    const wrapperStyle: React.CSSProperties = {
      position: 'relative',
      width: '100%',
      height: `${rowHeight}px`,
      background: backgroundColor,
      border: 'none',
      borderWidth: 0,
      borderStyle: 'none',
      borderColor: 'transparent',
      outline: isSelected ? '2px solid var(--color-primary-blue-light)' : 'none',
      boxShadow: 'none',
      WebkitBoxShadow: 'none',
      MozBoxShadow: 'none',
      borderRadius: 0,
      cursor: column.disabled ? 'not-allowed' : 'pointer'
    };

    const inputStyle: React.CSSProperties = {
      padding,
      fontSize: `${fontSize}px`,
      height: `${rowHeight}px`,
      width: '100%',
      border: 'none',
      outline: 'none',
      boxShadow: 'none',
      WebkitBoxShadow: 'none',
      MozBoxShadow: 'none',
      background: 'transparent',
      color: 'transparent',
      caretColor: 'transparent',
      fontWeight: 400,
      boxSizing: 'border-box',
      textAlign: column.textAlign || 'center',
      fontFamily: styleConfig.fontFamily,
      position: 'relative',
      zIndex: 2,
      // Move text off-screen but keep input functional
      textIndent: '-9999px',
      // Keep input visible and functional (opacity: 1)
      opacity: 1,
      pointerEvents: 'auto',
      WebkitTextFillColor: 'transparent'
    } as React.CSSProperties;

    const overlayStyle: React.CSSProperties = {
      position: 'absolute',
      inset: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent,
      padding,
      fontSize: `${fontSize}px`,
      fontFamily: styleConfig.fontFamily,
      fontWeight: 400,
      color: value ? textColor : placeholderColor,
      pointerEvents: 'none',
      zIndex: 1,
      textAlign: column.textAlign || 'center'
    };

    // Force normal font weight via DOM manipulation
    useEffect(() => {
      const overlayEl = document.querySelector(`.date-display-text[data-date="${value || ''}"]`);
      if (overlayEl) {
        (overlayEl as HTMLElement).style.setProperty('font-weight', '400', 'important');
      }
    }, [value, isSelected]);

    const combinedDateRef = (el: HTMLInputElement | null) => {
      dateInputRef.current = el;
      if (inputRef) inputRef(el);
    };

    const handleWrapperClick = () => {
      if (column.disabled) return;
      const inputEl = dateInputRef.current;
      if (!inputEl) return;
      
      // Record when picker is opened to detect auto-selection
      pickerOpenedAtRef.current = Date.now();
      
      if (typeof (inputEl as any).showPicker === 'function') {
        (inputEl as any).showPicker();
      } else {
        inputEl.focus();
        inputEl.click();
      }
    };

    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      const previousValue = previousValueRef.current;
      const timeSincePickerOpened = Date.now() - pickerOpenedAtRef.current;
      
      // Only block if: value is the same AND it happens very quickly after opening (< 100ms)
      // This prevents auto-selection when calendar opens, but allows all other interactions
      if (newValue === previousValue && timeSincePickerOpened < 100) {
        // This is auto-selection on open - ignore it
        // Don't manipulate DOM, just don't call onChange
        return;
      }
      
      // If value changed, process it immediately
      if (newValue !== previousValue) {
        // Update the ref and call onChange immediately
        previousValueRef.current = newValue;
        onChange(newValue);
      }
    };

    return (
      <div
        className="date-display-wrapper"
        style={wrapperStyle}
        onClick={handleWrapperClick}
      >
        <input
          ref={combinedDateRef}
          type="date"
          value={value || ''}
          className="date-display-input"
          style={inputStyle}
          onChange={handleDateChange}
          onKeyDown={onKeyDown}
          onFocus={(e) => {
            const el = e.target as HTMLInputElement;
            // Completely hide the native date input value on focus while keeping it functional
            el.style.setProperty('color', 'transparent', 'important');
            el.style.setProperty('caret-color', 'transparent', 'important');
            el.style.setProperty('text-indent', '-9999px', 'important');
            // Keep input functional (opacity: 1)
            el.style.setProperty('opacity', '1', 'important');
            el.style.setProperty('pointer-events', 'auto', 'important');
            el.style.setProperty('-webkit-text-fill-color', 'transparent', 'important');
            el.style.setProperty('-moz-text-fill-color', 'transparent', 'important');
            onFocus();
          }}
          onBlur={() => {
            // Update previous value when input loses focus to sync with any external changes
            if (dateInputRef.current) {
              previousValueRef.current = dateInputRef.current.value || '';
            }
          }}
          readOnly={!isEditable}
          disabled={column.disabled}
          title={column.description || column.label}
        />
        <span
          ref={overlayTextRef}
          className={`date-display-text ${value ? '' : 'date-display-empty'}`}
          style={overlayStyle}
          data-date={value || ''}
        >
          {formattedDisplay || placeholderText}
        </span>
      </div>
    );
  }

  // Render select dropdown - USING CUSTOM COMPONENT TO REMOVE BORDERS
  if (column.type === 'select' && column.options) {
    const selectStyle: React.CSSProperties = {
      padding,
      fontSize: `${fontSize}px`,
      height: `${rowHeight}px`,
      width: '100%',
      border: 'none',
      borderWidth: 0,
      borderStyle: 'none',
      borderColor: 'transparent',
      outline: 'none',
      boxShadow: 'none',
      background: column.calculated 
        ? 'var(--color-primary-blue-light)' 
        : value 
          ? 'var(--color-white)' 
          : 'var(--color-gray-50)',
      color: column.calculated 
        ? 'var(--color-primary-blue)' 
        : '#000000',
      fontWeight: 400,
      boxSizing: 'border-box',
      textAlign: column.textAlign || 'center',
      fontFamily: styleConfig.fontFamily
    };

    return (
      <CustomSelect
        value={value || ''}
        options={column.options}
        onChange={onChange}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        onBlur={() => {}}
        disabled={!isEditable || column.disabled}
        title={column.description || column.label}
        style={selectStyle}
        className="custom-select-no-border"
        inputRef={inputRef}
      />
    );
  }

  // Render readonly/calculated field
  if (!isEditable || column.type === 'readonly' || column.calculated) {
    const readonlyInputRef = useRef<HTMLInputElement>(null);
    
    // Force normal weight via DOM manipulation for ALL readonly inputs
    useEffect(() => {
      if (readonlyInputRef.current) {
        readonlyInputRef.current.style.setProperty('font-weight', '400', 'important');
      }
    }, [value, isSelected]);
    
    const displayValue = formatCellValue(value, column, rowData);
    const combinedReadonlyRef = (el: HTMLInputElement | null) => {
      readonlyInputRef.current = el;
      if (inputRef) inputRef(el);
    };
    
    return (
      <input
        ref={combinedReadonlyRef}
        type="text"
        value={displayValue}
        style={baseStyle}
        readOnly
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        title={column.calculated ? 'Auto-calculated' : column.description || column.label}
      />
    );
  }

  // Render number input - ensure normal weight and proper decimal formatting
  if (column.type === 'number') {
    const numberInputRef = useRef<HTMLInputElement>(null);
    const [isFocused, setIsFocused] = React.useState(false);
    const [inputValue, setInputValue] = React.useState('');
    
    // Format value for display based on column's decimalPlaces
    const decimalPlaces = column.decimalPlaces ?? 2;
    
    // Initialize and update input value when value prop changes (when not focused)
    useEffect(() => {
      if (!isFocused) {
        if (value == null || value === '') {
          setInputValue('');
        } else {
          const num = parseFloat(String(value));
          if (!isNaN(num)) {
            setInputValue(num.toFixed(decimalPlaces));
          } else {
            setInputValue(String(value));
          }
        }
      }
    }, [value, decimalPlaces, isFocused]);
    
    // Force normal weight via DOM manipulation for ALL inputs
    useEffect(() => {
      if (numberInputRef.current) {
        numberInputRef.current.style.setProperty('font-weight', '400', 'important');
      }
    }, [value, isSelected]);
    
    const numberStyle: React.CSSProperties = {
      ...baseStyle,
      fontWeight: 400
    };
    
    const combinedNumberRef = (el: HTMLInputElement | null) => {
      numberInputRef.current = el;
      if (inputRef) inputRef(el);
    };
    
    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      // Show raw numeric value when focused for editing (remove trailing zeros)
      if (value != null && value !== '') {
        const num = parseFloat(String(value));
        if (!isNaN(num)) {
          setInputValue(String(num));
        }
      }
      onFocus();
    };
    
    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      // Format value on blur and update
      const trimmedValue = e.target.value.trim();
      if (trimmedValue === '') {
        setInputValue('');
        onChange(null);
      } else {
        const num = parseFloat(trimmedValue);
        if (!isNaN(num)) {
          const formatted = num.toFixed(decimalPlaces);
          setInputValue(formatted);
          onChange(formatted);
        } else {
          // If invalid, revert to previous formatted value
          if (value != null && value !== '') {
            const prevNum = parseFloat(String(value));
            if (!isNaN(prevNum)) {
              const formatted = prevNum.toFixed(decimalPlaces);
              setInputValue(formatted);
            } else {
              setInputValue('');
            }
          } else {
            setInputValue('');
          }
        }
      }
    };
    
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      // Update local state for immediate feedback while editing
      setInputValue(e.target.value);
      // Also update parent (may be needed for calculations)
      onChange(e.target.value);
    };
    
    return (
      <input
        ref={combinedNumberRef}
        type="text"
        inputMode="decimal"
        value={inputValue}
        style={numberStyle}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={column.placeholder}
        title={column.description || column.label}
        disabled={column.disabled}
      />
    );
  }

  // Render text input (default) - ensure normal weight
  const textInputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = React.useState(false);
  const [inputValue, setInputValue] = React.useState('');
  
  // Initialize and update input value when value prop changes (when not focused)
  useEffect(() => {
    if (!isFocused) {
      if (value == null || value === '') {
        setInputValue('');
      } else {
        // Use formatCellValue for display when not focused
        const formatted = formatCellValue(value, column, rowData);
        setInputValue(formatted);
      }
    }
  }, [value, column, rowData, isFocused]);
  
  // Force normal weight via DOM manipulation for ALL inputs
  useEffect(() => {
    if (textInputRef.current) {
      textInputRef.current.style.setProperty('font-weight', '400', 'important');
    }
  }, [value, isSelected]);
  
  const textStyle: React.CSSProperties = {
    ...baseStyle,
    fontWeight: 400
  };
  
  const combinedTextRef = (el: HTMLInputElement | null) => {
    textInputRef.current = el;
    if (inputRef) inputRef(el);
  };
  
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    // Show raw value when focused for editing
    setInputValue(value || '');
    onFocus();
  };
  
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    // Parse and save the value
    const trimmedValue = e.target.value.trim();
    if (trimmedValue === '') {
      setInputValue('');
      onChange(null);
    } else {
      // Use parse function if available, otherwise use raw value
      const parsed = column.parse ? column.parse(trimmedValue) : trimmedValue;
      onChange(parsed);
    }
  };
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Update local state for immediate feedback while editing
    setInputValue(e.target.value);
  };
  
  return (
    <input
      ref={combinedTextRef}
      type="text"
      value={inputValue}
      style={textStyle}
      onChange={handleChange}
      onKeyDown={onKeyDown}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={column.placeholder}
      title={column.description || column.label}
      readOnly={!isEditable}
      disabled={column.disabled}
    />
  );
};

/**
 * `onChange`/`onKeyDown`/`onFocus`/`inputRef` are recreated every time the parent
 * `TableRow` renders (they close over this row's key/index), but they're functionally
 * identical across those renders since a mounted row's key/index don't change — so we
 * intentionally don't compare them here. `rowData` is only compared for calculated
 * columns, whose displayed value can depend on sibling fields; plain columns only need
 * their own `value` to match, since `rowData`'s reference changes for the whole row
 * even when a single sibling field is edited.
 */
const arePropsEqual = (prev: TableCellProps, next: TableCellProps): boolean => {
  if (prev.column !== next.column) return false;
  if (prev.value !== next.value) return false;
  if (prev.width !== next.width) return false;
  if (prev.styleConfig !== next.styleConfig) return false;
  if (prev.isSelected !== next.isSelected) return false;
  if (prev.isEditable !== next.isEditable) return false;
  if (prev.column.calculated && prev.rowData !== next.rowData) return false;
  return true;
};

export const TableCell = React.memo(TableCellComponent, arePropsEqual);
